import { ErhlLoanPurpose } from '@/helpers/const';

import type { ErhlLoanPurposeValue } from '@/helpers/const';

/** Sentinel value for "no additional borrowing purposes" in the additional-purposes multi-select. */
export const ADDITIONAL_PURPOSES_NO = 'no' as const;

const ERHL_LOAN_PURPOSE_VALUES = new Set<string>(Object.values(ErhlLoanPurpose));

const isErhlLoanPurposeValue = (value: string): value is ErhlLoanPurposeValue => ERHL_LOAN_PURPOSE_VALUES.has(value);

export interface ResolveErhlLoanPurposesArgs {
  isPurchaseApplication: boolean;
  showAdditionalBorrowingPurposesQuestion: boolean;
  /** Raw selected additional purposes from the form; may include the "no" sentinel. */
  selectedAdditional: string[];
  existingErhlLoanPurposes: ErhlLoanPurposeValue[];
}

/**
 * Rebuilds the submitted ERHL loan purposes array. When the additional-purposes question is
 * hidden, purposes already carried on the application are preserved instead of being dropped.
 * Additional values are validated against the ErhlLoanPurpose enum, so the "no" sentinel,
 * unknown strings, and a duplicate purchase_new_property can never leak into the result.
 */
export function resolveErhlLoanPurposes({
  isPurchaseApplication,
  showAdditionalBorrowingPurposesQuestion,
  selectedAdditional,
  existingErhlLoanPurposes,
}: ResolveErhlLoanPurposesArgs): ErhlLoanPurposeValue[] {
  if (!isPurchaseApplication) return existingErhlLoanPurposes;

  const source = showAdditionalBorrowingPurposesQuestion ? selectedAdditional : existingErhlLoanPurposes;
  const additional = source.filter(
    (p): p is ErhlLoanPurposeValue => isErhlLoanPurposeValue(p) && p !== ErhlLoanPurpose.PURCHASE_NEW_PROPERTY,
  );

  return [...new Set([ErhlLoanPurpose.PURCHASE_NEW_PROPERTY, ...additional])];
}
